import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import { Clock, Trash2, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '@/utils/api';

export const HistoryModal = ({ isOpen, onClose, onRescan }) => {
  const { t } = useTranslation();
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) loadHistory();
  }, [isOpen]);

  const loadHistory = async () => {
    setIsLoading(true);
    try {
      const response = await api.get('/history/list');
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const removeItem = async (barcode) => {
    try {
      await api.delete(`/history/${barcode}`);
      setHistory(history.filter(h => h.barcode !== barcode));
    } catch (error) {
      console.error('Error removing history item:', error);
    }
  };

  const clearAll = async () => {
    if (!window.confirm(t('confirmClearHistory', '¿Borrar todo el historial?'))) return;
    try {
      await api.delete('/history');
      setHistory([]);
    } catch (error) {
      console.error('Error clearing history:', error);
    }
  };

  const rescan = (barcode) => {
    onClose();
    if (onRescan) onRescan(barcode);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-6 h-6 text-[#8FBC8F]" />
            {t('scanHistory', 'Historial de escaneos')}
            {history.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="ml-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="clear-history-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {t('clearHistory', 'Borrar todo')}
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#8FBC8F]" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-gray-500" data-testid="history-empty">
              {t('noHistoryYet', 'Aún no has escaneado ningún producto')}
            </div>
          ) : (
            history.map((item) => (
              <Card
                key={item.barcode}
                className="hover:shadow-md transition-shadow"
                data-testid={`history-item-${item.barcode}`}
              >
                <CardContent className="p-3 flex items-center gap-3">
                  {item.image_url ? (
                    <img
                      src={item.image_url}
                      alt={item.product_name}
                      className="w-16 h-16 object-contain flex-shrink-0 bg-gray-50 rounded"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-100 rounded flex-shrink-0 flex items-center justify-center text-gray-400 text-xs">
                      Sin foto
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {item.safe_to_consume ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0" />
                      )}
                      <h3 className="font-semibold text-sm truncate">{item.product_name}</h3>
                    </div>
                    {item.brands && (
                      <p className="text-xs text-gray-500 truncate">{item.brands}</p>
                    )}
                    {!item.safe_to_consume && item.allergens_detected?.length > 0 && (
                      <p className="text-xs text-red-600 mt-0.5 truncate">
                        ⚠ {item.allergens_detected.join(', ')}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">
                      {item.barcode} · {formatDate(item.scanned_at)}
                    </p>
                  </div>

                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => rescan(item.barcode)}
                      data-testid={`rescan-${item.barcode}`}
                      className="text-xs"
                    >
                      Ver
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeItem(item.barcode)}
                      data-testid={`remove-history-${item.barcode}`}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
